import React from 'react';
import { ExternalLink, X } from 'lucide-react';
import AdvertiserTermsContent from './AdvertiserTermsContent';

interface AdvertiserTermsModalProps {
  onClose: () => void;
  // Publisher accent, used only for the top rail so the overlay reads as part
  // of the branded wizard. The terms copy itself is publisher-neutral.
  accentColor?: string;
}

// In-page overlay that renders the CTVBuyer Advertiser Terms without leaving
// the wizard. Same copy as the standalone /advertiser-terms page (both render
// AdvertiserTermsContent). Closes on backdrop click, the X button, or Escape.
const AdvertiserTermsModal: React.FC<AdvertiserTermsModalProps> = ({ onClose, accentColor = '#3b82f6' }) => {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] z-50"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="CTVBuyer Advertiser Terms"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92%] max-w-[560px] max-h-[85vh] flex flex-col bg-white rounded-2xl border border-slate-200/80 overflow-hidden"
        style={{ boxShadow: '0 24px 48px -16px rgba(15,23,42,0.28), 0 2px 4px -1px rgba(15,23,42,0.05)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-[3px] shrink-0"
          style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }}
        />
        <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3 border-b border-slate-100 shrink-0">
          <h2 className="text-[15px] font-semibold text-slate-900 tracking-tight">
            CTVBuyer Advertiser Terms
          </h2>
          <div className="flex items-center gap-3">
            <a
              href="/advertiser-terms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-900 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open page
            </a>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-slate-400 hover:text-slate-900 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="px-5 py-5 overflow-y-auto">
          <AdvertiserTermsContent />
        </div>
      </div>
    </>
  );
};

export default AdvertiserTermsModal;
